import React from 'react';
import Img from '../components/Img';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Affiliations from '../components/Affiliations';
import Footer from '../components/Footer';
import IgniteLogo from '../components/IgniteLogo';
import SEO from '../components/SEO';
import {
  about,
  researchThemes,
  news,
  ignite,
  values,
  team,
  trialsOfficeTeam,
  trials,
} from '../mock';

const JOURNAL_TAGS = [
  'The Lancet',
  'European Heart Journal',
  'Nature Communications',
  'Circulation',
  'JACC',
  'JAMA Cardiology',
  'Hypertension',
];

const ORCID_URL = 'https://orcid.org/0000-0001-6921-1592';
const PUBMED_URL = 'https://pubmed.ncbi.nlm.nih.gov/?term=Cheriyan+j+and+cambridge+or+cheriyan+j+and+gsk&sort=date';

export default function Home() {
  const topResearch = researchThemes.slice(0, 4);
  // Sort news newest-first by ISO date if available, else by year from tag
  // (mid-year fallback) so the home widget always surfaces the freshest items.
  const topNews = (() => {
    const key = (n) => {
      const d = String(n.date || '').trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
      const m = String(n.tag || '').match(/(19|20)\d{2}/);
      return m ? `${m[0]}-06-15` : '0000-00-00';
    };
    return [...news]
      .sort((a, b) => key(b).localeCompare(key(a)))
      .slice(0, 3);
  })();
  const teamPreview = team.slice(0, 8);
  const totalTeam = team.length + trialsOfficeTeam.length;
  const ongoingTrialsCount =
    trials.academic.filter((t) => /[–-]\s*$/.test(t.years)).length +
    trials.commercial.filter((t) => /[–-]\s*$/.test(t.years)).length;

  return (
    <div>
      <SEO
        title="Dr Joseph Cheriyan | Cardiovascular Trials, Cambridge"
        description="Cambridge clinical research group led by Dr Joseph Cheriyan: early-phase trials, experimental medicine and AI-enabled cardiovascular research."
        path="/"
      />
      <Navbar />
      <main>
        <Hero />
        <Affiliations />

        {/* Accreditations */}
        <section
          id="accreditations"
          className="bg-[#F7F8FA] py-14 md:py-16 border-b border-[#ECEEF1]"
          data-testid="home-accreditations-section"
        >
          <div className="container-x">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-6">
              Accreditations
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <a
                href="https://www.eshonline.org/esh-centres/excellence-centres/"
                target="_blank"
                rel="noreferrer"
                data-testid="home-accreditation-esh"
                className="flex items-center gap-5 bg-white border border-[#ECEEF1] p-5 md:p-6 hover:border-[#133844] transition-colors"
              >
                <Img
                  src="/logos/esh.png"
                  alt="European Society of Hypertension logo"
                  className="h-16 w-auto shrink-0 object-contain"
                />
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#133844] font-semibold">
                    European Society of Hypertension
                  </div>
                  <div className="heading-serif text-[#232830] text-[20px] leading-tight mt-1">
                    Recognised ESH Hypertension Centre of Excellence
                  </div>
                </div>
              </a>
              <a
                href="https://www.escardio.org/about-us/who-we-are/supporting-excellence/accreditation/eacvi-laboratory-accreditation-in-echocardiography/eacvi-echo-accredited-laboratories/"
                target="_blank"
                rel="noreferrer"
                data-testid="home-accreditation-eacvi"
                className="flex items-center gap-5 bg-white border border-[#ECEEF1] p-5 md:p-6 hover:border-[#133844] transition-colors"
              >
                <Img
                  src="/logos/eacvi.png"
                  alt="European Association of Cardiovascular Imaging (EACVI) logo"
                  className="h-16 w-auto shrink-0 object-contain"
                />
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#133844] font-semibold">
                    European Association of Cardiovascular Imaging
                  </div>
                  <div className="heading-serif text-[#232830] text-[20px] leading-tight mt-1">
                    Accredited EACVI Laboratory
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Principal Investigator */}
        <section
          id="principal-investigator"
          className="bg-white py-16 md:py-20 border-b border-[#ECEEF1]"
          data-testid="home-pi-section"
        >
          <div className="container-x">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-3">
              Principal Investigator
            </div>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-8">
                <h2 className="heading-serif text-[#232830] text-[34px] md:text-[42px] leading-[1.05]">
                  {about.name}
                </h2>
                <div className="mt-2 text-[13px] tracking-[0.14em] uppercase text-[#546072]">
                  {about.credentials}
                </div>

                <p className="mt-5 text-[#232830] leading-relaxed text-[15px] max-w-2xl">
                  {about.intro}
                </p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/about" data-testid="home-full-bio" className="btn-primary">
                    Full profile <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/publications"
                    data-testid="home-publications-link"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#133844] text-[#133844] text-[13.5px] font-semibold hover:bg-[#D1F9F1] transition-colors"
                  >
                    Publications <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-4">
                <Img
                  src={about.image}
                  alt={about.name}
                  className="w-full max-w-[320px] h-auto object-cover border border-[#ECEEF1]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Research themes */}
        <section
          id="research"
          className="bg-[#F7F8FA] py-20 md:py-24 border-b border-[#ECEEF1]"
          data-testid="home-research-section"
        >
          <div className="container-x">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-3">
                  What we study
                </div>
                <h2 className="heading-serif text-[#232830] text-[36px] md:text-[44px] leading-[1.05]">
                  Cardiovascular research themes
                </h2>
              </div>
              <Link to="/research" className="btn-ghost-light">
                Research overview <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {topResearch.map((r, i) => (
                <Link
                  to="/research"
                  key={r.name}
                  className="group relative block bg-white border border-[#ECEEF1] hover:border-[#133844] transition-all p-7 md:p-8"
                  data-testid={`home-research-card-${i}`}
                >
                  <div className="flex items-baseline gap-3">
                    <span className="heading-serif text-[#8EE8D8] text-[42px] leading-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#133844] font-semibold">
                      Theme
                    </span>
                  </div>
                  <h3 className="heading-serif text-[#232830] text-[22px] md:text-[24px] mt-4 leading-snug">
                    {r.name}
                  </h3>
                  <p className="mt-4 text-[#546072] text-[13.5px] leading-relaxed">
                    {r.description.split(/\.\s+/)[0]}.
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-[#133844] text-[13px] font-semibold">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* IGNITE — flagship initiative (teaser) */}
        <section
          id="ignite"
          className="bg-white py-16 md:py-20 border-b border-[#ECEEF1]"
          data-testid="home-ignite-section"
        >
          <div className="container-x">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-3">
              Flagship initiative
            </div>
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <IgniteLogo height={44} />
                <p className="mt-5 text-[#232830] leading-relaxed text-[15.5px] max-w-2xl">
                  {ignite.tagline}
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link to="/ignite" className="btn-primary" data-testid="home-ignite-cta">
                  Visit IGNITE <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Lab values — compressed inline strip */}
        <section
          id="values"
          className="bg-white py-8 md:py-10 border-b border-[#ECEEF1]"
          data-testid="home-values-section"
        >
          <div className="container-x flex flex-col md:flex-row md:items-baseline gap-x-6 gap-y-2">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] shrink-0 md:pt-1">
              Lab values
            </div>
            <div className="text-[#232830] text-[15px] md:text-[16px] leading-relaxed">
              {values.map((v, i) => (
                <span key={v}>
                  <span className="text-[#133844]">{v}</span>
                  {i < values.length - 1 && (
                    <span className="mx-2 text-[#B5BDC8]">·</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Team preview */}
        <section
          id="team"
          className="bg-white py-20 md:py-24 border-b border-[#ECEEF1]"
          data-testid="home-team-section"
        >
          <div className="container-x">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-3">
                  The people
                </div>
                <h2 className="heading-serif text-[#232830] text-[36px] md:text-[44px] leading-[1.05]">
                  Meet the Cambridge clinical research team
                </h2>
              </div>
              <Link to="/team" className="btn-ghost-light">
                Full team <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
              {teamPreview.map((m) => (
                <div key={m.name} className="group">
                  <div className="aspect-square overflow-hidden bg-[#ECEEF1] border border-[#ECEEF1]">
                    {m.img ? (
                      <Img
                        src={m.img}
                        alt={m.name}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#8B95A3] text-[11px] tracking-[0.14em] uppercase">
                        Photo pending
                      </div>
                    )}
                  </div>
                  <div className="mt-3 text-[14px] font-semibold text-[#232830] leading-tight">
                    {m.name}
                  </div>
                  <div className="mt-1 text-[12.5px] text-[#546072] leading-snug">
                    {m.role}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/team"
                className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#133844] hover:underline"
                data-testid="home-team-view-all"
              >
                View all {totalTeam} team members <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* News */}
        <section
          id="news"
          className="bg-[#F7F8FA] py-20 md:py-24 border-b border-[#ECEEF1]"
          data-testid="home-news-section"
        >
          <div className="container-x">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-3">
                  Latest
                </div>
                <h2 className="heading-serif text-[#232830] text-[36px] md:text-[44px] leading-[1.05]">
                  News &amp; updates
                </h2>
              </div>
              <Link to="/news" className="btn-ghost-light">
                All news <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {topNews.map((n, i) => (
                <Link
                  to="/news"
                  key={i}
                  className="group block bg-white border border-[#ECEEF1] hover:border-[#133844] transition-all p-6"
                  data-testid={`home-news-card-${i}`}
                >
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#133844] font-semibold">
                    {n.tag}
                  </div>
                  <h3 className="mt-3 heading-serif text-[#232830] text-[20px] leading-snug group-hover:text-[#133844]">
                    {n.title}
                  </h3>
                  <p className="mt-3 text-[#546072] text-[13.5px] leading-relaxed">
                    {(n.excerpt || '').split(/\.\s+/).slice(0, 1).join('. ')}{n.excerpt && !n.excerpt.endsWith('.') ? '.' : ''}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[#133844] text-[13px] font-semibold">
                    Read more <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Publications teaser */}
        <section
          id="publications"
          className="bg-white py-20 md:py-24"
          data-testid="home-publications-section"
        >
          <div className="container-x">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-6">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-3">
                  Publications
                </div>
                <h2 className="heading-serif text-[#232830] text-[36px] md:text-[44px] leading-[1.05]">
                  Peer-reviewed cardiovascular research
                </h2>
                <p className="mt-6 text-[#232830] leading-relaxed text-[15px] max-w-xl">
                  The group&apos;s work spans high-impact journals in cardiovascular medicine,
                  clinical pharmacology and translational science. Browse the full
                  publication record via ORCID or PubMed — updated live.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/publications"
                    className="btn-primary"
                    data-testid="home-publications-link"
                  >
                    View publications <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <a
                    href={ORCID_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#133844] text-[#133844] text-[13.5px] font-semibold hover:bg-[#D1F9F1] transition-colors"
                  >
                    ORCID <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={PUBMED_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#133844] text-[#133844] text-[13.5px] font-semibold hover:bg-[#D1F9F1] transition-colors"
                  >
                    PubMed <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#546072] mb-4">
                  Featured journals
                </div>
                <div className="flex flex-wrap gap-2">
                  {JOURNAL_TAGS.map((j) => (
                    <span
                      key={j}
                      className="inline-block px-3 py-1.5 bg-[#F7F8FA] border border-[#ECEEF1] text-[13px] text-[#232830]"
                    >
                      {j}
                    </span>
                  ))}
                </div>
                <div className="mt-8 p-5 border border-[#ECEEF1] bg-[#F7F8FA]">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#133844] font-semibold">
                    Currently
                  </div>
                  <div className="mt-2 heading-serif text-[#232830] text-[20px] leading-snug">
                    {ongoingTrialsCount} active clinical studies
                  </div>
                  <p className="mt-2 text-[13.5px] text-[#546072] leading-relaxed">
                    Chief or Principal Investigator on ongoing academic and commercial
                    trials at the Cambridge Biomedical Campus.
                  </p>
                  <Link
                    to="/trials"
                    className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-[#133844] font-semibold hover:underline"
                  >
                    Trials portfolio <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
